/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { MAGIC_VALUE } from './../Constants';
import { IMessageDescriptor } from '@hcecbp/i18n-utils';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  IAny,
  toastMessageTypes
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { apiSuccessStatusCode, postApiSuccess } from '../Constants';

const d = descriptors.energyManagement;
const u = descriptors.urjanetStatus;
const Z = descriptors.ZLOOnboardingConfig;

export const getNotifyInfo = (
  apiResponse: IAny
): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  try {
    if (apiResponse.data[0].title.toLowerCase() === 'success') {
      return {
        notifyDescription: apiResponse.data[0].detail,
        notifyType: 'success'
      };
    } else {
      return {
        notifyDescription: apiResponse.data[0].detail,
        notifyType: 'error'
      };
    }
  } catch {
    return {
      notifyDescription: d.UPDATEERRORMESSAGE,
      notifyType: 'error'
    };
  }
};

export const getNotifyUtilityInfo = (apiResponse: {
  data: IAny;
  statusCode: number;
}): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  try {
    if (
      apiResponse.statusCode === MAGIC_VALUE.TWOHUNDRED ||
      apiResponse.statusCode === MAGIC_VALUE.TWOHUNDREDONE
    ) {
      if (
        typeof apiResponse?.data === 'object' &&
        apiResponse?.data?.data &&
        apiResponse?.data?.data?.length > 0
      ) {
        return {
          notifyDescription: apiResponse.data.data[0].detail,
          notifyType: 'success'
        };
      }
      return {
        notifyDescription: apiResponse.data,
        notifyType: 'success'
      };
    } else {
      return {
        notifyDescription: apiResponse.data,
        notifyType: 'error'
      };
    }
  } catch {
    return {
      notifyDescription: d.UPDATEERRORMESSAGE,
      notifyType: 'error'
    };
  }
};

export const getEnrollNotifyInfo = (
  response: IAny
): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  const errorObj = {
    notifyDescription: d.UPDATEERRORMESSAGE,
    notifyType: 'error' as toastMessageTypes
  };
  try {
    if (typeof response === 'string') {
      return {
        notifyDescription: response,
        notifyType: 'success'
      };
    } else if (response?.rawResponse?.response?.status === postApiSuccess) {
      return {
        notifyDescription: u.ENROLLED,
        notifyType: 'success'
      };
    } else if (typeof response?.rawResponse?.response?.data === 'string') {
      return {
        notifyDescription: response.rawResponse.response.data,
        notifyType: 'error'
      };
    } else {
      return errorObj;
    }
  } catch {
    return errorObj;
  }
};

export const getConversionNotifyInfo = (
  response: IAny
): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  const errorObj = {
    notifyDescription: d.UPDATEERRORMESSAGE,
    notifyType: 'error' as toastMessageTypes
  };
  try {
    if (typeof response === 'string') {
      return {
        notifyDescription: d.MANUALTOAUTOMATICCONVERSION,
        notifyType: 'success'
      };
    } else if (response?.rawResponse?.response?.status === postApiSuccess) {
      return {
        notifyDescription: u.ENROLLED,
        notifyType: 'success'
      };
    } else if (
      response?.rawResponse?.response?.status === apiSuccessStatusCode
    ) {
      return {
        notifyDescription: d.MANUALTOAUTOMATICCONVERSION,
        notifyType: 'success'
      };
    } else if (typeof response?.rawResponse?.response?.data === 'string') {
      return {
        notifyDescription: response.rawResponse.response.data,
        notifyType: 'error'
      };
    } else {
      return errorObj;
    }
  } catch {
    return errorObj;
  }
};

export const getBaselineNotifyInfo = (
  response: IAny
): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  const errorObj = {
    notifyDescription: d.UPDATEERRORMESSAGE,
    notifyType: 'error' as toastMessageTypes
  };
  try {
    if (typeof response === 'string') {
      return {
        notifyDescription: d.BASELINE_SUCCESS,
        notifyType: 'success'
      };
    } else if (
      response?.rawResponse?.response?.status === postApiSuccess ||
      response.status === postApiSuccess
    ) {
      return {
        notifyDescription: d.BASELINE_SUCCESS,
        notifyType: 'success'
      };
    } else if (typeof response?.rawResponse?.response?.data === 'string') {
      return {
        notifyDescription: response.rawResponse.response.data,
        notifyType: 'error'
      };
    } else {
      return errorObj;
    }
  } catch {
    return errorObj;
  }
};

/**
 * Return Notification Type and Message String
 * @param response Response of update request
 * @returns {notifyDescription: string | IMessageDescriptor, notifyType: toastMessageTypes}
 */
export const getMeterNameChangeNotifyInfo = (
  response: IAny
): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  const errorObj = {
    notifyDescription: d.UPDATEERRORMESSAGE,
    notifyType: 'error' as toastMessageTypes
  };
  try {
    if (typeof response === 'string') {
      return {
        notifyDescription: d.METERNAMEUPDATESUCCESS,
        notifyType: 'success'
      };
    } else if (
      response?.rawResponse?.response?.status === apiSuccessStatusCode
    ) {
      return {
        notifyDescription: d.METERNAMEUPDATESUCCESS,
        notifyType: 'success'
      };
    } else if (response?.rawResponse?.response?.status === postApiSuccess) {
      return {
        notifyDescription: d.METERNAMEUPDATESUCCESS,
        notifyType: 'success'
      };
    } else if (typeof response?.rawResponse?.response?.data === 'string') {
      return {
        notifyDescription:
          response.rawResponse.response.data || d.UPDATEERRORMESSAGE,
        notifyType: 'error'
      };
    } else {
      return errorObj;
    }
  } catch {
    return errorObj;
  }
};

/**
 * Return Notification Type and Message String
 * @returns {notifyDescription: string | IMessageDescriptor, notifyType: toastMessageTypes}
 */
export const getZLONotifyInfo = (): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  const errorObj = {
    notifyDescription: Z.SUBMISSION_SUCCESS_SUBTITLE,
    notifyType: 'success' as toastMessageTypes
  };
  return errorObj;
};

export const getNotifyForEnableIDS = (apiResponse: {
  data: IAny;
  statusCode: number;
}): {
  notifyDescription: string | IMessageDescriptor;
  notifyType: toastMessageTypes;
} => {
  try {
    if (
      apiResponse.statusCode === MAGIC_VALUE.TWOHUNDRED ||
      apiResponse.statusCode === MAGIC_VALUE.TWOHUNDREDONE
    ) {
      if (
        typeof apiResponse?.data === 'object' &&
        apiResponse?.data?.data &&
        apiResponse?.data?.data?.length > 0
      ) {
        return {
          notifyDescription: apiResponse.data.data[0].detail,
          notifyType: 'success'
        };
      }
      return {
        notifyDescription: apiResponse.data,
        notifyType: 'success'
      };
    } else {
      return {
        notifyDescription: apiResponse.data,
        notifyType: 'error'
      };
    }
  } catch {
    return {
      notifyDescription: d.UPDATEERRORMESSAGE,
      notifyType: 'error'
    };
  }
};
